import {
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
  type JSX
} from 'react'

import { CardEntry } from './components/CardEntry.tsx'
import { CancelSpacer, LoadingScreen } from './components/LoadingScreen.tsx'
import { MethodList } from './components/MethodList.tsx'
import { PaymentConfirm } from './components/PaymentConfirm.tsx'
import { PaymentOutcome } from './components/PaymentOutcome.tsx'
import type { CardFieldsAdapter } from './lib/cardFields.ts'
import { describeExpiry, describeMethod } from './lib/format.ts'
import type { TerminalStatus } from './lib/protocol.ts'
import {
  TerminalApiError,
  messageFor,
  type TerminalApi
} from './lib/terminalApi.ts'
import { ancestorsAllowed, type OriginsLookup } from './lib/terminalOrigins.ts'
import {
  createTerminalChannel,
  type ChannelWindow,
  type TerminalChannel
} from './lib/terminalChannel.ts'
import {
  MAX_SUBMISSIONS,
  PAYMENT_IN_FLIGHT,
  initialState,
  reduce,
  takesPayment,
  type Status,
  type TerminalEvent
} from './lib/terminalMachine.ts'
import { themeToCssVars, vgsFieldCss, type Theme } from './lib/theme.ts'
import {
  ACTION_CARD_CREATE,
  ACTION_LIST,
  ACTION_PAYMENT_CREATE,
  type AttemptState,
  type CardDraft,
  type SessionConfig
} from './lib/types.ts'

const NO_THEME: Theme = {}

export interface AppProps {
  win: ChannelWindow
  terminalId: string | null
  api: TerminalApi
  createCardFields: () => CardFieldsAdapter
  lookupOrigins: (terminalId: string) => Promise<OriginsLookup>
  initialLookup?: OriginsLookup
  newKey?: () => string
  now?: () => number
  pollMs?: number
  maxPolls?: number
}

const randomKey = (): string => crypto.randomUUID()

function openedLookup(lookup: OriginsLookup | undefined): {
  gate: 'pending' | 'open' | 'unknown' | 'unavailable'
  origins: readonly string[] | null
  theme: Theme | undefined
} {
  if (lookup?.kind === 'found') {
    return { gate: 'open', origins: lookup.origins, theme: lookup.theme }
  }
  if (lookup?.kind === 'unknown') {
    return { gate: 'unknown', origins: null, theme: undefined }
  }
  if (lookup?.kind === 'error') {
    return { gate: 'unavailable', origins: null, theme: undefined }
  }
  return { gate: 'pending', origins: null, theme: undefined }
}

const HOST_STATUS: Partial<Record<Status, TerminalStatus>> = {
  exchanging: 'loading',
  method_list: 'awaiting_input',
  card_entry: 'awaiting_input',
  submitting: 'submitting',
  quoting: 'loading',
  confirm: 'awaiting_input',
  paying: 'submitting',
  recovering: 'submitting',
  declined: 'awaiting_input'
}

const POLL_MS = 2_000
const MAX_POLLS = 45

const EXPIRY_WARNING_MS = 60_000
// A renewal init resets the screen, so never ask for one mid-save, mid-payment or after the result.
const RENEWAL_HELD: ReadonlySet<Status> = new Set<Status>([
  'submitting',
  ...PAYMENT_IN_FLIGHT,
  'payment_unknown',
  'success',
  'closed'
])

const CANCELLABLE: ReadonlySet<Status> = new Set<Status>([
  'method_list',
  'card_entry',
  'confirm',
  'declined',
  'payment_unknown',
  'error'
])

const BUSY: ReadonlySet<Status> = new Set<Status>([
  'submitting',
  'quoting',
  ...PAYMENT_IN_FLIGHT
])

function failed(generation: number, error: unknown): TerminalEvent {
  const code = error instanceof TerminalApiError ? error.code : 'unavailable'
  return { type: 'FAILED', generation, code, message: messageFor(code) }
}

function lost(generation: number, attemptId: number): TerminalEvent {
  return {
    type: 'PAYMENT_LOST',
    generation,
    attemptId,
    message: messageFor('payment_unknown')
  }
}

function settled(
  generation: number,
  attemptId: number,
  attempt: AttemptState
): TerminalEvent | null {
  switch (attempt.status) {
    case 'succeeded':
      return {
        type: 'PAYMENT_SUCCEEDED',
        generation,
        attemptId,
        payment: attempt.payment
      }
    case 'declined':
      return {
        type: 'PAYMENT_DECLINED',
        generation,
        attemptId,
        decline: attempt.decline
      }
    default:
      return null
  }
}

/**
 * The terminal: one screen at `/t/:terminalId`, driven by the host over postMessage.
 * @param props - The window, terminal id, API client and card field factory.
 * @returns The terminal screen.
 */
export function App(props: AppProps): JSX.Element {
  const { win, terminalId, api, createCardFields, lookupOrigins } = props
  const newKey = props.newKey ?? randomKey
  const now = props.now ?? Date.now
  const pollMs = props.pollMs ?? POLL_MS
  const maxPolls = props.maxPolls ?? MAX_POLLS
  const [state, dispatch] = useReducer(reduce, initialState)
  const channelRef = useRef<TerminalChannel | null>(null)
  const tokenRef = useRef<{ generation: number; idToken: string } | null>(null)
  const submittedRef = useRef<CardDraft | null>(null)
  const quotedRef = useRef<object | null>(null)
  const paidRef = useRef<string | null>(null)
  const initHeldRef = useRef(false)
  const seeded = openedLookup(props.initialLookup)
  const [gate, setGate] = useState(seeded.gate)
  const [allowedOrigins, setAllowedOrigins] = useState<
    readonly string[] | null
  >(seeded.origins)
  const [lookupTheme, setLookupTheme] = useState<Theme | undefined>(
    seeded.theme
  )
  const [trackedId, setTrackedId] = useState(terminalId)
  // main already awaited this id. Skipping that fetch keeps the channel from restarting.
  const [seededId, setSeededId] = useState(
    props.initialLookup !== undefined ? terminalId : null
  )
  const [renewalDue, setRenewalDue] = useState<SessionConfig | null>(null)
  const renewedRef = useRef<SessionConfig | null>(null)
  const rootRef = useRef<HTMLElement | null>(null)
  const heightRef = useRef<number | null>(null)
  if (terminalId !== trackedId) {
    setTrackedId(terminalId)
    setGate('pending')
    setAllowedOrigins(null)
    setLookupTheme(undefined)
    setSeededId(null)
  }

  useEffect(() => {
    if (terminalId === null || seededId === terminalId) return undefined
    let cancelled = false
    void lookupOrigins(terminalId).then(result => {
      if (cancelled) return
      if (result.kind === 'found') {
        setAllowedOrigins(result.origins)
        setLookupTheme(result.theme)
        setGate('open')
        return
      }
      setLookupTheme(undefined)
      setGate(result.kind === 'unknown' ? 'unknown' : 'unavailable')
    })
    return () => {
      cancelled = true
    }
  }, [lookupOrigins, seededId, terminalId])

  const ancestorRefused =
    allowedOrigins !== null &&
    !ancestorsAllowed(win.location?.ancestorOrigins, allowedOrigins)

  useEffect(() => {
    if (terminalId === null || gate !== 'open' || allowedOrigins === null) {
      return undefined
    }
    if (ancestorRefused) return undefined
    const id = terminalId

    const channel = createTerminalChannel(
      win,
      {
        canInit: () => !initHeldRef.current,
        onPaymentInterrupted() {
          initHeldRef.current = true
          dispatch({
            type: 'PAYMENT_INTERRUPTED',
            message: messageFor('payment_unknown')
          })
        },
        onInit(clientSecret, parentOrigin, generation) {
          // A charge may be in flight on the current token. Keep it and drop this init.
          if (initHeldRef.current) return
          tokenRef.current = null
          dispatch({ type: 'INIT', generation })
          api
            .exchange({ clientSecret, parentOrigin, terminalId: id })
            .then(({ idToken, session }) => {
              if (channel.generation() !== generation) return undefined
              tokenRef.current = { generation, idToken }
              channel.markExchanged(generation)
              const listing = session.actions.includes(ACTION_LIST)
                ? api.listMethods(idToken)
                : Promise.resolve([])
              return listing.then(methods => {
                dispatch({
                  type: 'SESSION_LOADED',
                  generation,
                  session,
                  methods
                })
              })
            })
            .catch((error: unknown) => dispatch(failed(generation, error)))
        },
        onClose() {
          dispatch({ type: 'CLOSE' })
        }
      },
      { allowedOrigins }
    )
    channelRef.current = channel
    // The first layout can come before the channel. The channel posts it when init locks the origin.
    if (heightRef.current !== null) channel.resize(heightRef.current)
    channel.start()
    dispatch({ type: 'READY_POSTED' })
    return () => {
      channel.stop()
      channelRef.current = null
    }
  }, [allowedOrigins, ancestorRefused, api, gate, terminalId, win])

  const { status, draft, generation, payment } = state
  const paymentSession = takesPayment(state)
  const paymentLocked =
    PAYMENT_IN_FLIGHT.has(status) ||
    (paymentSession && status === 'submitting') ||
    state.error?.code === 'payment_unknown' ||
    state.result?.action === ACTION_PAYMENT_CREATE
  useEffect(() => {
    initHeldRef.current = paymentLocked
  }, [paymentLocked])

  useEffect(() => {
    const hostStatus =
      status === 'error' && paymentSession
        ? 'awaiting_input'
        : HOST_STATUS[status]
    if (hostStatus)
      channelRef.current?.post('state', {
        status: hostStatus,
        ...(paymentSession && status !== 'submitting'
          ? { action: ACTION_PAYMENT_CREATE }
          : {})
      })
  }, [status, paymentSession])

  useEffect(() => {
    if (state.error) channelRef.current?.post('error', { ...state.error })
  }, [state.error])

  useEffect(() => {
    if (state.result) channelRef.current?.post('success', { ...state.result })
  }, [state.result])

  useEffect(() => {
    if (status !== 'submitting' || !draft) return
    if (submittedRef.current === draft) return
    submittedRef.current = draft
    const token = tokenRef.current
    if (!token || token.generation !== generation) {
      dispatch(failed(generation, new TerminalApiError('session_expired')))
      return
    }
    api.createCardMethod(token.idToken, draft, newKey()).then(
      method => dispatch({ type: 'METHOD_CREATED', generation, method }),
      (error: unknown) => dispatch(failed(generation, error))
    )
  }, [status, draft, generation, api, newKey])

  useEffect(() => {
    if (status !== 'quoting' || !payment || quotedRef.current === payment) {
      return
    }
    quotedRef.current = payment
    const token = tokenRef.current
    if (!token || token.generation !== generation) {
      dispatch(failed(generation, new TerminalApiError('session_expired')))
      return
    }
    const { paymentMethodId } = payment
    api.createPaymentAttempt(token.idToken, paymentMethodId).then(
      quote => dispatch({ type: 'QUOTED', generation, paymentMethodId, quote }),
      (error: unknown) => dispatch(failed(generation, error))
    )
  }, [status, payment, generation, api])

  useEffect(() => {
    if (status !== 'paying' || !payment?.quote || !payment.idempotencyKey) {
      return
    }
    const { attemptId } = payment.quote
    const submission = `${payment.idempotencyKey}:${payment.submissions}`
    if (paidRef.current === submission) return
    paidRef.current = submission
    const first = payment.submissions === 1
    const token = tokenRef.current
    if (!token || token.generation !== generation) {
      dispatch(
        first
          ? failed(generation, new TerminalApiError('session_expired'))
          : lost(generation, attemptId)
      )
      return
    }
    api.submitPayment(token.idToken, attemptId, payment.idempotencyKey).then(
      outcome => {
        if (outcome.kind === 'succeeded') {
          dispatch({
            type: 'PAYMENT_SUCCEEDED',
            generation,
            attemptId,
            payment: outcome.payment
          })
        } else if (outcome.kind === 'declined') {
          dispatch({
            type: 'PAYMENT_DECLINED',
            generation,
            attemptId,
            decline: outcome.decline
          })
        } else {
          dispatch({ type: 'PAYMENT_UNKNOWN', generation, attemptId })
        }
      },
      (error: unknown) => {
        // An earlier submit of this attempt may have charged, so a later refusal proves nothing.
        dispatch(
          first ? failed(generation, error) : lost(generation, attemptId)
        )
      }
    )
  }, [status, payment, generation, api])

  const recoveringAttempt =
    status === 'recovering' ? (payment?.quote?.attemptId ?? null) : null
  const submissions = payment?.submissions ?? 0
  useEffect(() => {
    if (recoveringAttempt === null) return undefined
    const attemptId = recoveringAttempt
    let cancelled = false
    let polls = 0
    let timer: ReturnType<typeof setTimeout> | undefined
    function poll(): void {
      const token = tokenRef.current
      if (cancelled) return
      if (!token || token.generation !== generation || polls >= maxPolls) {
        dispatch(lost(generation, attemptId))
        return
      }
      polls += 1
      api.readPaymentAttempt(token.idToken, attemptId).then(
        attempt => {
          if (cancelled) return
          const event = settled(generation, attemptId, attempt)
          if (event) dispatch(event)
          else if (
            attempt.status === 'pending' &&
            submissions < MAX_SUBMISSIONS
          ) {
            dispatch({ type: 'RESUBMIT', generation, attemptId })
          } else timer = setTimeout(poll, pollMs)
        },
        (error: unknown) => {
          if (cancelled) return
          const code = error instanceof TerminalApiError ? error.code : null
          if (code === 'session_expired' || code === 'access_denied') {
            dispatch(lost(generation, attemptId))
          } else timer = setTimeout(poll, pollMs)
        }
      )
    }
    timer = setTimeout(poll, pollMs)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [recoveringAttempt, submissions, generation, api, pollMs, maxPolls])

  useEffect(() => {
    const current = state.session
    const expiresAt = current
      ? Date.parse(current.sessionExpiresAt)
      : Number.NaN
    if (!current || Number.isNaN(expiresAt)) return undefined
    const remaining = expiresAt - now()
    if (remaining <= 0) return undefined
    const delay = Math.max(remaining - EXPIRY_WARNING_MS, remaining / 2)
    const timer = setTimeout(() => setRenewalDue(current), delay)
    return () => clearTimeout(timer)
  }, [state.session, now])

  useEffect(() => {
    if (!renewalDue || renewalDue !== state.session) return
    if (RENEWAL_HELD.has(status) || renewedRef.current === renewalDue) return
    renewedRef.current = renewalDue
    channelRef.current?.post('session_expiring', {})
  }, [renewalDue, state.session, status])

  useEffect(() => {
    const el = rootRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(() => {
      const height = Math.ceil(el.getBoundingClientRect().height)
      heightRef.current = height
      channelRef.current?.resize(height)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  function cancel(): void {
    channelRef.current?.post('close', {})
    dispatch({ type: 'CLOSE' })
  }

  const theme = state.session?.theme ?? lookupTheme ?? NO_THEME
  useLayoutEffect(() => {
    const pageStyle = document.documentElement.style
    const previous = Object.entries(themeToCssVars(theme)).map(
      ([key, value]) => {
        const old = pageStyle.getPropertyValue(key)
        pageStyle.setProperty(key, value)
        return [key, old] as const
      }
    )
    return () => {
      for (const [key, value] of previous) {
        if (value) pageStyle.setProperty(key, value)
        else pageStyle.removeProperty(key)
      }
    }
  }, [theme])
  const actions = state.session?.actions ?? []
  const canChangeCard = reduce(state, { type: 'CHANGE_CARD' }) !== state
  const canRetry = reduce(state, { type: 'RETRY' }) !== state
  const canGoBackToList = reduce(state, { type: 'BACK' }) !== state

  function body(): JSX.Element {
    if (terminalId === null || gate === 'unknown') {
      return (
        <p
          role="alert"
          data-testid="terminal-unknown"
          className="blytz-alert blytz-alert--danger"
        >
          {'This terminal link is not valid.'}
        </p>
      )
    }
    if (gate === 'open' && ancestorRefused) {
      return (
        <p
          role="alert"
          data-testid="terminal-refused"
          className="blytz-alert blytz-alert--danger"
        >
          {messageFor('origin_rejected')}
        </p>
      )
    }
    if (gate === 'unavailable') {
      return (
        <p
          role="alert"
          data-testid="terminal-error"
          className="blytz-alert blytz-alert--danger"
        >
          {messageFor('unavailable')}
        </p>
      )
    }
    if (gate !== 'open') return <LoadingScreen />
    switch (state.status) {
      case 'method_list':
        return (
          <MethodList
            methods={
              paymentSession
                ? state.methods.filter(m => m.paymentType === 'card')
                : state.methods
            }
            canCreate={actions.includes(ACTION_CARD_CREATE)}
            onSelect={paymentMethodId =>
              dispatch({ type: 'METHOD_SELECTED', paymentMethodId })
            }
            onAdd={() => dispatch({ type: 'OPEN_CARD_ENTRY' })}
          />
        )
      case 'card_entry':
      case 'submitting':
        return (
          <CardEntry
            createCardFields={createCardFields}
            fieldCss={vgsFieldCss(theme)}
            canGoBack={actions.includes(ACTION_LIST)}
            saving={state.status === 'submitting'}
            onTokenized={tokenized =>
              dispatch({ type: 'CARD_TOKENIZED', generation, draft: tokenized })
            }
            onBack={() => dispatch({ type: 'BACK' })}
          />
        )
      case 'quoting':
        return (
          <p
            role="status"
            data-testid="payment-quoting"
            className="blytz-alert"
          >
            {'Preparing the payment…'}
          </p>
        )
      case 'confirm':
      case 'paying':
        return state.payment?.quote ? (
          <PaymentConfirm
            quote={state.payment.quote}
            locale={state.session?.locale ?? 'en-US'}
            paying={state.status === 'paying'}
            canChangeCard={canChangeCard}
            onPay={() => {
              initHeldRef.current = true
              dispatch({ type: 'PAY', idempotencyKey: newKey() })
            }}
            onChangeCard={() => dispatch({ type: 'CHANGE_CARD' })}
          />
        ) : (
          <LoadingScreen />
        )
      case 'recovering':
        return <PaymentOutcome kind="recovering" />
      case 'payment_unknown':
        return (
          <PaymentOutcome
            kind="unknown"
            message={state.error?.message ?? messageFor('payment_unknown')}
          />
        )
      case 'declined':
        return state.payment?.decline ? (
          <PaymentOutcome
            kind="declined"
            decline={state.payment.decline}
            onChangeCard={() => dispatch({ type: 'CHANGE_CARD' })}
          />
        ) : (
          <LoadingScreen />
        )
      case 'success':
        if (state.result?.action === ACTION_PAYMENT_CREATE) {
          return (
            <PaymentOutcome
              kind="paid"
              result={state.result}
              locale={state.session?.locale ?? 'en-US'}
            />
          )
        }
        return (
          <div className="flex flex-col gap-3">
            <p
              data-testid="terminal-success"
              className="blytz-alert blytz-alert--success"
            >
              {state.result?.status === 'created'
                ? 'Card saved: '
                : 'Selected: '}
              {state.result ? describeMethod(state.result) : ''}
            </p>
            {state.result && (
              <div className="blytz-card blytz-card--selected">
                <p className="blytz-pm__label">
                  {describeMethod(state.result)}
                </p>
                <p className="blytz-pm__meta">
                  {describeExpiry(state.result.expMonth, state.result.expYear)}
                </p>
              </div>
            )}
          </div>
        )
      case 'error':
        return (
          <div className="flex flex-col gap-3">
            <p
              role="alert"
              data-testid="terminal-error"
              className="blytz-alert blytz-alert--danger"
            >
              {state.error?.message}
            </p>
            <div className="flex flex-col gap-2">
              {canRetry && (
                <button
                  type="button"
                  data-testid="terminal-retry"
                  className="blytz-btn blytz-btn--primary blytz-btn--full"
                  onClick={() => dispatch({ type: 'RETRY' })}
                >
                  {'Try again'}
                </button>
              )}
              {canGoBackToList && (
                <button
                  type="button"
                  data-testid="terminal-back-to-list"
                  className="blytz-btn blytz-btn--outline blytz-btn--full"
                  onClick={() => dispatch({ type: 'BACK' })}
                >
                  {'Back to cards'}
                </button>
              )}
            </div>
          </div>
        )
      case 'closed':
        return (
          <p data-testid="terminal-closed" className="blytz-alert">
            {'The terminal is closed.'}
          </p>
        )
      default:
        return <LoadingScreen />
    }
  }

  return (
    <main
      ref={rootRef}
      data-testid="terminal-root"
      data-status={state.status}
      style={themeToCssVars(theme)}
      className="terminal-root"
    >
      {body()}
      {gate === 'open' && CANCELLABLE.has(state.status) && (
        <div className="flex justify-center">
          <button
            type="button"
            data-testid="terminal-cancel"
            className="blytz-btn blytz-btn--link"
            onClick={cancel}
          >
            {'Cancel'}
          </button>
        </div>
      )}
      {gate === 'open' && BUSY.has(state.status) && <CancelSpacer />}
      <div
        role="status"
        aria-live="polite"
        className="sr-only"
        data-testid="terminal-live"
      >
        {state.status === 'success' ? 'Done.' : ''}
      </div>
    </main>
  )
}
